/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/require-computed-property-dependencies, getter-return, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { alias } from '@ember/object/computed';
import SchoolStatus from '@linc-technologies/linced-common/enums/school-status';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import CalendarEventCode from '@linc-technologies/linced-common/enums/calendar-event-code';
import SchoolDatesDays from 'linced-app/components/school-dates-days';
import SchoolDatesDaysRange from 'linced-app/components/school-dates-days-range';
import config from 'linced-app/config/environment';
import moment from 'moment';
import { observes } from '@ember-decorators/object';
import { TrackedArray } from 'tracked-built-ins';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class DatesController extends Controller {
  @action
  setTerm(value) {
    this.term = value;
  }

  @action
  setYear(value) {
    this.year = value;
  }

  queryParams = ['year'];
  @tracked
  year = 0;

  // Services
  @service
  dates;

  @service
  fetch;

  @service
  session;

  @service
  store;

  @service
  toaster;

  Jurisdiction = Jurisdiction;
  CalendarEventCode = CalendarEventCode;
  @tracked
  dateGenerationInProgress = false;
  // @tracked: was a plain, non-tracked field, but addTimeframe/removeTimeframe
  // both reassign it (see below) - route's setupController and this default
  // already agree it's a plain array (not A()), so this was purely a missing
  // @tracked bug, not an array-type one. Reassignment correctly invalidates
  // the classic @computed('timeframes.@each...') getters below, same as the
  // already-proven dateErrors/dateErrorsPreview pattern elsewhere.
  @tracked
  timeframes = [];

  @alias('session.school')
  school;

  @alias('school.jurisdiction')
  jurisdiction;

  @tracked
  holidayDialogOpen = false;
  @tracked
  saveInProgress = false;

  @computed('model.{from,to}')
  get yearLabel() {
    let fromYear = moment(this.model.from).year();
    let toYear = moment(this.model.to).year();
    if (fromYear === toYear) {
      // Must be stringy to facilitate filtering.
      return `${fromYear}`;
    }
    return `${fromYear}-${toYear}`;
  }

  @computed('model.{from,to}')
  get months() {
    let months = [];
    let month = moment(this.model.from).date(1);
    let end = moment(this.model.to);
    while (month.isSameOrBefore(end, 'day')) {
      months.push({
        center: moment(month),
      });
      month.add(1, 'month');
    }

    return months;
  }

  @tracked term = 1;
  @tracked maxTerm = 4;
  @tracked editingTerm = false;

  get calendarComponent() {
    if (this.editingTerm) {
      return 'power-calendar-range';
    }
    return 'power-calendar-multiple';
  }

  get daysComponent() {
    if (this.editingTerm) {
      return SchoolDatesDaysRange;
    }
    return SchoolDatesDays;
  }

  // Plain getter (not @computed): terms is TrackedArray-backed, and
  // dateSelected() mutates it via native .splice() rather than reassigning
  // it, so this needs to read the live index directly under autotracking
  // rather than a classic '@each'/'.[]' dependent key (which doesn't
  // reliably observe TrackedArray's native mutations - see terms below).
  get selectedDates() {
    if (this.editingTerm) {
      // Return a range for the selected term
      let term = parseInt(this.term, 10);
      return this.terms[term];
    }
    // No "selected" dates for default view (handled by custom school-dates-days component)
  }

  // terms stays a classic @computed deliberately: its cache is used as
  // mutable term-editing state, not a purely derived value.
  // dateSelected() mutates the cached TrackedArray in place via
  // .splice() (rather than reassigning `terms`), which only re-renders
  // consumers that read it via autotracking (see selectedDates above) -
  // a plain (uncached) getter would instead rebuild `terms` from
  // model.dates on every read and silently discard that in-progress
  // selection.
  @computed('editingTerm', 'model.dates.{[],@each.term}')
  get terms() {
    let dates = this.get('model.dates');
    let terms = new TrackedArray();
    let maxTerm = this.maxTerm;
    if (dates) {
      for (var term = 1; term <= maxTerm; term++) {
        let termDates = dates.filter((d) => d.term === term);
        if (termDates.length) {
          termDates = termDates
            .slice()
            .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
          terms[term] = {
            start: moment(termDates[0].get('date')),
            end: moment(termDates[termDates.length - 1].get('date')),
          };
        } else {
          terms[term] = {
            start: null,
            end: null,
          };
        }
      }
    }
    return terms;
  }

  @computed('maxTerm', 'terms.[]', 'model.dates.@each.{status,term}')
  get openCounts() {
    let counts = [];
    for (let i = 1; i <= this.maxTerm; i++) {
      counts.push({
        term: i,
        label: `Term ${i}`,
        totalDays: 0,
        halfDays: 0,
      });
    }
    counts.push({
      term: 'total',
      label: `Year Totals`,
      totalDays: 0,
      halfDays: 0,
    });

    return this.model.dates.reduce((counts, date) => {
      let wholeDay = false;
      let halfDay = false;

      switch (date.status) {
        case SchoolStatus.OPEN:
          wholeDay = true;
          break;
        case SchoolStatus.MORNING:
        case SchoolStatus.AFTERNOON:
          halfDay = true;
          break;
      }

      if (wholeDay || halfDay) {
        if (date.term) {
          let termCounts = counts.find((item) => item.term === date.term);
          if (!isEmpty(termCounts)) {
            if (wholeDay) {
              termCounts.totalDays++;
              // Increment half days once here, then again after this `if`.
              termCounts.halfDays++;
            }
            termCounts.halfDays++;
          }

          let totals = counts.find((item) => item.term === 'total');
          if (!isEmpty(totals)) {
            if (wholeDay) {
              totals.totalDays++;
              // Increment half days once here, then again after this `if`.
              totals.halfDays++;
            }
            totals.halfDays++;
          }
        }
      }
      return counts;
    }, counts);
  }

  @observes('term')
  _enforceTermRange() {
    let term = this.term;
    let maxTerm = this.maxTerm;
    if (term < 1) {
      this.set('term', 1);
    } else if (term > maxTerm) {
      this.set('term', maxTerm);
    }
  }

  @computed('timeframes.@each.hasDirtyAttributes', 'timeframesValid')
  get canSave() {
    // Do we have any existing dirty timeframes?
    if (
      this.timeframes.length !== 0 &&
      this.timeframes.some((timeframe) => timeframe.hasDirtyAttributes)
    ) {
      // Are they valid?
      return this.timeframesValid;
    }

    return false;
  }

  @computed(
    'timeframes.@each.{columnLabel,dateValidate}',
    'timeframeOverlaps.[]',
  )
  get timeframesValid() {
    // Do we have any overlapping timeframes
    if (this.get('timeframeOverlaps.length')) {
      return false;
    }
    // Do any of our timeframes have missing labels or invalid dates?
    return this.timeframes.every((timeframe) => {
      return (
        !isEmpty(timeframe.columnLabel) &&
        timeframe.get('dateValidate') &&
        timeframe.get('yearValidate')
      );
    });
  }

  // Build a array of indexes of overlapping timeframes
  // This makes them easy to identify in the templates
  @computed('timeframes.@each.{startDate,endDate}')
  get timeframeOverlaps() {
    let overlapIndexes = [];
    this.timeframes.forEach((timeframe, index) => {
      if (
        !isEmpty(this.timeframes[index + 1]) &&
        this.timeframes[index].endDate >= this.timeframes[index + 1].startDate
      ) {
        overlapIndexes.push(index);
      }
    });
    return overlapIndexes;
  }

  // Clean up the new, unsaved timeframe when we exit the route
  // in case it gets picked up from the store by another route
  cleanupTimeframes() {
    this.timeframes.forEach((timeframe) => {
      if (isEmpty(timeframe.get('id')) && !timeframe.isDeleted) {
        timeframe.destroyRecord();
      }
    });
  }

  @computed('school.yearLevels.[]')
  get allYearLevels() {
    let yearLevels = this.school.yearLevels.slice();
    yearLevels.unshift('ALL');
    return yearLevels;
  }

  @computed('model.dates.@each.{date,statusClass,dateExplained}')
  get datesToExplain() {
    let dates = [];
    this.model.dates.forEach((date) => {
      let day = moment(date.date).day();
      if (
        day !== 0 &&
        day !== 6 &&
        date.status === SchoolStatus.CLOSED &&
        date.term &&
        !date.dateExplained
      ) {
        dates.push(date);
      }
    });
    return dates;
  }

  @computed('model.dates.@each.dateExplained')
  get datesExplained() {
    let dates = [];
    this.model.dates.forEach((date) => {
      if (date.dateExplained) {
        dates.push(date);
      }
    });
    return dates;
  }

  // allDates returns an array of unexplained and explained dates
  @computed('datesToExplain.[]', 'datesExplained.[]')
  get allDates() {
    return this.datesToExplain.concat(this.datesExplained);
  }

  @action
  closeHolidayDialog() {
    // Reset contents before closing dialog
    this.allDates.forEach((date) => rollbackRecord(date));
    this.holidayDialogOpen = false;
  }

  @action
  addYear(date, year) {
    let holidayYears = date.get('holidayYears') || [];
    if (!holidayYears.includes(year)) {
      date.set('holidayYears', [...holidayYears, year]);
    }
  }

  @action
  removeYear(date, year) {
    date.set(
      'holidayYears',
      (date.holidayYears || []).filter((y) => y !== year),
    );
  }

  @action
  setCalendarEventCode(date, option) {
    date.set('calendarEventCode', option.value);
  }

  @action
  save(dates) {
    this.saveInProgress = true;

    let savePromises = dates.map((date) => {
      date.setProperties({
        dateExplained: true,
      });
      return date.save();
    });

    Promise.all(savePromises)
      .then(() => {
        return this.model.dates.update();
      })
      .then(() => {
        this.toaster.success('Holidays reported successfully!');
        this.toggleProperty('holidayDialogOpen');
      })
      .catch(() => {
        this.toaster.error('Error reporting holidays.');
      })
      .finally(() => {
        this.saveInProgress = false;
      });
  }

  // updateTimeframe writes one of the timeframe record's editable attributes.
  // The record and attribute name are curried in by `{{fn}}` so the input's
  // value arrives last; `@attr` fields are tracked, so assignment is enough to
  // invalidate the `timeframes.@each.{...}` computeds above.
  @action
  updateTimeframe(timeframe, attribute, value) {
    timeframe[attribute] = value;
  }

  @action
  removeTimeframe(timeframe) {
    timeframe
      .destroyRecord()
      .then(() => {
        // remove timeframe from timeframes array
        this.timeframes = this.timeframes.filter((t) => t !== timeframe);
        this.toaster.success(`Timeframe deleted successfully.`);
      })
      .catch(() => {
        this.toaster.error(`Timeframe delete failed.`);
      });
  }

  @action
  addTimeframe() {
    let properties = {
      schoolId: this.get('session.school'),
      year: this.year.toString(),
    };
    this.timeframes = [
      ...this.timeframes,
      this.store.createRecord('timeframe', properties),
    ];
  }

  @action
  saveTimeframes() {
    let promises = [];
    this.timeframes.forEach((timeframe) => {
      promises.push(timeframe.save());
    });
    Promise.all(promises)
      .then(() => {
        this.toaster.success(`Timeframes saved successfully.`);
      })
      .catch(() => {
        this.toaster.error(`Error saving timeframes.`);
      });
  }

  @action
  toggleTermEdit(shouldUpdate) {
    if (this.editingTerm && shouldUpdate) {
      // Exiting term editing mode - update dates from terms
      this.updateTerms();
    }

    this.toggleProperty('editingTerm');
  }

  @action
  dateSelected(day, calendar, date) {
    if (this.editingTerm) {
      let term = parseInt(this.term, 10);
      this.terms.splice(term, 1, day);
    } else {
      this.updateDay(date);
    }
  }

  updateTerms() {
    let dates = this.get('model.dates');
    let terms = this.terms;

    // Reset term and week to 0 for all records
    dates.forEach((date) => date.setProperties({ term: 0, week: 0 }));

    // Set term based on selected ranges
    terms.forEach(function (range, term) {
      if (isEmpty(range.start) || isEmpty(range.end)) {
        return;
      }
      let d = moment(range.start);
      let firstWeek = d.week();
      while (!d.isAfter(range.end)) {
        let rec = dates.find((item) => item.date === d.format('YYYY-MM-DD'));
        let weekday = d.format('ddd');
        rec.set('term', term);
        rec.set('week', d.week() - firstWeek + 1);

        // Open school by default during term (on weekdays; closed on weekends)
        if (!rec.get('status')) {
          if (weekday === 'Sat' || weekday === 'Sun') {
            rec.set('status', 2);
          } else {
            rec.set('status', 1);
          }
        }

        d.add(1, 'd');
      }
    });

    // Reset any days which are now outside the term boundaries.
    dates.forEach((date) => {
      if (date.term === 0 && date.status !== 0) {
        date.set('status', 0);
      }
    });

    // Persist updated dates in bulk.
    let url = `${config.APP.apiHost}/dates/${config.APP.apiPath}/dates/:bulkupdate`;
    let data = {
      schoolId: this.session.activeSchool,
      dates: dates.map((date) => date.serialize({ includeId: true })),
    };
    return this.fetch
      .post(url, { data })
      .then(() => {
        // Fetch updated date records to reset dirty states.
        return this.model.dates.update();
      })
      .then(() => {
        this.toaster.success(`Term boundaries updated successfully!`);
      })
      .catch(() => {
        this.toaster.error(`Error saving term boundaries.`);
      });
  }

  updateDay(date) {
    if (date.get('status') === 4) {
      if (date.get('term')) {
        date.set('status', 1);
      } else {
        // If outside a term, roll around to "unspecified"
        date.set('status', 0);
      }
    } else {
      date.incrementProperty('status');
    }
    // Clear holiday details for open dates
    if (date.get('status') !== SchoolStatus.CLOSED) {
      date.setProperties({
        holidayDescription: '',
        holidayYears: [],
        dateExplained: false,
        calendarEventCode: null,
      });
    }
    date.save();
  }

  // generateDates populates the data store with a record for each day of a given year.
  // This is called from the Route's setupController hook if no dates were returned,
  // but may also be useful for generating templates.
  generateDates(model) {
    this.dateGenerationInProgress = true;
    let d = moment(model.from);
    let end = moment(model.to);
    let dates = [];

    while (!d.isAfter(end)) {
      let date = d.format('YYYY-MM-DD');

      // Increment date now, in case we skip creating this date.
      d.add(1, 'd');

      if (!isEmpty(model.dates.find((item) => item.date === date))) {
        // We already have this date; don't try to recreate it.
        continue;
      }

      let rec = {
        schoolId: this.get('session.activeSchool'),
        date,
        status: 0,
        week: 0,
        term: 0,
      };

      dates.push(rec);
    }

    let data = {
      schoolId: this.session.activeSchool,
      dates,
      // Skip publishing an event when generating "empty" dates for a new year.
      // This prevents wiping a school's calendar in SIF, for instance.
      skipEvent: true,
    };
    let bulkUrl = `${config.APP.apiHost}/dates/${config.APP.apiPath}/dates/:bulk`;
    return this.fetch
      .post(bulkUrl, { data })
      .then(() => {
        // Fetch newly created records.
        return this.model.dates.update();
      })
      .then(() => {
        // Once the new dates are loaded, mark generation as complete.
        this.dateGenerationInProgress = false;
      });
  }
}
